import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

// The page the password-reset email links to. Supabase's detectSessionInUrl
// parses the recovery token from the URL and establishes a short-lived
// recovery session; we wait for that session, then let the user set a new
// password via updateUser({ password }).

export const Route = createFileRoute('/auth/reset')({
  component: ResetPasswordPage,
});

const RECOVERY_TIMEOUT_MS = 8_000;

const resetSchema = z
  .object({
    password: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type ResetInput = z.infer<typeof resetSchema>;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [linkState, setLinkState] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetInput>({
    resolver: zodResolver(resetSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    let cancelled = false;

    async function waitForRecoverySession() {
      const initial = await supabase.auth.getSession();
      if (cancelled) return;
      if (initial.data.session) {
        setLinkState('ready');
        return;
      }
      // detectSessionInUrl resolves async on slow connections — listen for it.
      const timer = setTimeout(() => {
        subscription.unsubscribe();
        if (!cancelled) setLinkState('invalid');
      }, RECOVERY_TIMEOUT_MS);

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        if (session && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN')) {
          clearTimeout(timer);
          subscription.unsubscribe();
          if (!cancelled) setLinkState('ready');
        }
      });
    }

    void waitForRecoverySession();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(values: ResetInput) {
    setStatus('submitting');
    setServerError(null);
    const { error } = await supabase.auth.updateUser({ password: values.password });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[auth/reset] updateUser failed', { code: error.code, status: error.status });
      setStatus('error');
      setServerError("We couldn't update your password. The reset link may have expired.");
      return;
    }
    toast({
      title: 'Password updated',
      description: 'Sign in with your new password.',
      variant: 'success',
    });
    await navigate({ to: '/login' });
  }

  return (
    <main id="main" className="flex min-h-dvh items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <h2 className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-3xl)]">
          Set a new password
        </h2>

        {linkState === 'checking' && (
          <p className="mt-8 text-[var(--color-ink-muted)] text-[var(--text-base)]" role="status">
            Verifying your reset link…
          </p>
        )}

        {linkState === 'invalid' && (
          <div className="mt-8 space-y-4">
            <p className="text-[var(--color-ink-muted)] text-[var(--text-base)]">
              This reset link is invalid or has expired. Request a fresh one to continue.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block text-[var(--color-accent)] hover:underline"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {linkState === 'ready' && (
          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
            className="mt-8 space-y-5"
          >
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p
                  id="password-error"
                  role="alert"
                  className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                >
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p
                  id="confirm-error"
                  role="alert"
                  className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                >
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={!isValid || status === 'submitting'}
              aria-busy={status === 'submitting'}
              className="w-full"
            >
              {status === 'submitting' ? 'Updating…' : 'Update password'}
            </Button>

            {serverError && (
              <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
                {serverError}
              </p>
            )}
          </form>
        )}
      </div>
    </main>
  );
}
